import React, { useState } from "react";
import {
  Box,
  IconButton,
  Menu,
  Typography,
  Divider,
  Stack,
  Grid,
  Link,
  Avatar,
} from "@mui/material";
import AppsIcon from "@mui/icons-material/Apps";
import AgileEngineIcon from "./images/agileEngineIcon.svg";
import RockyIcon from "./images/rockyIcon.svg";
import RockyAiIcon from "./images/rockyAiIcon.svg";
import RockyFinanceIcon from "./images/rockyFinanceIcon.svg";
import HiringDashboardIcon from "./images/launchPodIcon.svg";
import BookOutlinedIcon from "@mui/icons-material/BookOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import PeopleOutlineOutlinedIcon from "@mui/icons-material/PeopleOutlineOutlined";
import { useQuery } from "@tanstack/react-query";
import { useAuthFetch } from "../../helpers/useAuthFetch";

enum OpenIconPositionOnScreen {
  TopLeft = "top-left",
  BottomLeft = "bottom-left",
  TopRight = "top-right",
  BottomRight = "bottom-right",
}

interface CrossLinksMenuProps {
  user?: {
    name: string;
    position: string;
    imgSrc: string;
  };
  referAFriendLink?: string;
}

export const CrossLinksMenu: React.FC<CrossLinksMenuProps> = ({
  user,
  referAFriendLink,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [openIconPosition, setOpenIconPosition] = useState(
    OpenIconPositionOnScreen.TopLeft
  );

  const fetchWithAuth = useAuthFetch();

  const {
    data: perrmissionsOnApss,
    error,
    isLoading,
  } = useQuery<{
    isRockyFinanceAvailable: boolean;
    isRockyAiAvailable: boolean;
    isHiringDashboardAvailable: boolean;
  }>({
    queryKey: ["permissions"],
    queryFn: () => fetchWithAuth("users/current-user/permissions-on-apps"),
  });

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    const menuIcon = event.currentTarget.getBoundingClientRect();

    const isBottomHalf = menuIcon.bottom > window.innerHeight / 2;
    const isRightHalf = menuIcon.right > window.innerWidth / 2;
    const isLeftHalf = menuIcon.left < window.innerWidth / 2;
    const isTopHalf = menuIcon.top < window.innerHeight / 2;

    if (isBottomHalf && isLeftHalf) {
      setOpenIconPosition(OpenIconPositionOnScreen.BottomLeft);
    } else if (isTopHalf && isRightHalf) {
      setOpenIconPosition(OpenIconPositionOnScreen.TopRight);
    } else if (isTopHalf && isLeftHalf) {
      setOpenIconPosition(OpenIconPositionOnScreen.TopLeft);
    } else if (isBottomHalf && isRightHalf) {
      setOpenIconPosition(OpenIconPositionOnScreen.BottomRight);
    }

    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const menuIconElement = (
    <AppsIcon
      sx={{
        color: "#0000008A",
      }}
    />
  );

  const directionOfContent = [
    OpenIconPositionOnScreen.BottomLeft,
    OpenIconPositionOnScreen.BottomRight,
  ].includes(openIconPosition)
    ? "column-reverse"
    : "column";

  const isContentReversed = directionOfContent === "column-reverse";

  return (
    <Box>
      <IconButton onClick={handleOpen}>{menuIconElement}</IconButton>

      <Menu
        anchorEl={anchorEl}
        anchorOrigin={
          [
            OpenIconPositionOnScreen.TopLeft,
            OpenIconPositionOnScreen.BottomLeft,
          ].includes(openIconPosition)
            ? {
                vertical:
                  openIconPosition === OpenIconPositionOnScreen.TopLeft
                    ? "top"
                    : "bottom",
                horizontal: "left",
              }
            : {
                vertical:
                  openIconPosition === OpenIconPositionOnScreen.TopRight
                    ? "top"
                    : "bottom",
                horizontal: "right",
              }
        }
        transformOrigin={
          [
            OpenIconPositionOnScreen.TopLeft,
            OpenIconPositionOnScreen.BottomLeft,
          ].includes(openIconPosition)
            ? {
                vertical:
                  openIconPosition === OpenIconPositionOnScreen.TopLeft
                    ? "top"
                    : "bottom",
                horizontal: "left",
              }
            : {
                vertical:
                  openIconPosition === OpenIconPositionOnScreen.TopRight
                    ? "top"
                    : "bottom",
                horizontal: "right",
              }
        }
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            maxWidth: 300,
          },
        }}
        MenuListProps={{
          style: {
            padding: 0,
          },
        }}>
        <Stack flexDirection={directionOfContent} alignItems={"revert"}>
          <IconButton
            onClick={handleClose}
            color='inherit'
            sx={{
              marginLeft: [
                OpenIconPositionOnScreen.BottomRight,
                OpenIconPositionOnScreen.TopRight,
              ].includes(openIconPosition)
                ? "auto"
                : undefined,
              marginRight: [
                OpenIconPositionOnScreen.BottomLeft,
                OpenIconPositionOnScreen.TopLeft,
              ].includes(openIconPosition)
                ? "auto"
                : undefined,
            }}>
            {menuIconElement}
          </IconButton>
          <Stack
            sx={{
              padding: "24px",
              paddingTop: !isContentReversed ? "0px" : "24px",
              paddingBottom: isContentReversed ? "0px" : "24px",
              gap: "16px",
            }}>
            <Stack alignItems='center'>
              <img src={AgileEngineIcon} />
            </Stack>
            <Stack flexDirection='row' alignItems='center'>
              <Divider
                sx={{
                  flexGrow: 1,
                }}
              />
              <Typography
                variant='subtitle2'
                color='textSecondary'
                sx={{
                  padding: "0px 7px",
                }}>
                Internal tools
              </Typography>
              <Divider
                sx={{
                  flexGrow: 1,
                }}
              />
            </Stack>

            <Grid container>
              {[
                {
                  icon: <img src={RockyIcon} />,
                  title: "Rocky",
                  href: "https://rocky.agileengine.com/",
                  display: "flex",
                },
                {
                  icon: <img src={RockyFinanceIcon} />,
                  title: "Finance",
                  href: "https://rocky-finance.agileengine.com/",
                  display: perrmissionsOnApss?.isRockyFinanceAvailable
                    ? "flex"
                    : "none",
                },
                {
                  icon: <img src={RockyAiIcon} />,
                  title: "Rocky AI",
                  href: "https://rocky-ai.agileengine.com/",
                  display: perrmissionsOnApss?.isRockyAiAvailable
                    ? "flex"
                    : "none",
                },
                {
                  icon: <img src={HiringDashboardIcon} />,
                  title: "Hiring \nDashboard",
                  href: "https://launchpod-dashboard.agileengine.com/",
                  display: perrmissionsOnApss?.isHiringDashboardAvailable
                    ? "flex"
                    : "none",
                },
              ].map((item) => (
                <Grid
                  key={item.title}
                  item
                  xs={6}
                  sx={{
                    display: item.display,
                    justifyContent: "center",
                  }}>
                  <IconButton
                    target='_blank'
                    component={Link}
                    href={item.href}
                    sx={{
                      borderRadius: "16px",
                      padding: "6px 24px",
                    }}>
                    <Stack direction='column' alignItems='center'>
                      {item.icon}
                      <Typography
                        variant='body2'
                        sx={{
                          whiteSpace: "pre-wrap",
                        }}>
                        {item.title}
                      </Typography>
                    </Stack>
                  </IconButton>
                </Grid>
              ))}
            </Grid>
            <Divider />
            <Stack gap='2px'>
              {[
                {
                  icon: <BookOutlinedIcon color='action' />,
                  title: "AgileEngine internal site",
                  href: "https://sites.google.com/agileengine.com/agileengine-ua/home",
                },
                {
                  icon: <HandshakeOutlinedIcon color='action' />,
                  title: "AgileEngine policies",
                  href: "https://sites.google.com/agileengine.com/agileengine-ua/home/ae-policies",
                },
                {
                  icon: <PeopleOutlineOutlinedIcon color='action' />,
                  title: "Refer a friend",
                  href:
                    referAFriendLink ??
                    "https://launchpod.agileengine.com/refer-a-friend",
                },
              ].map((item) => (
                <Link
                  target='_blank'
                  key={item.title}
                  sx={{
                    textDecoration: "none",
                    color: "inherit",

                    padding: "8px 4px",
                    borderRadius: "4px",

                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                  href={item.href}>
                  <Stack flexDirection='row' alignItems='center' gap='16px'>
                    {item.icon} {item.title}
                  </Stack>
                </Link>
              ))}
            </Stack>
            {user && (
              <>
                <Divider />
                <Stack
                  alignItems='center'
                  flexDirection='row'
                  gap='8px'
                  mb='12px'
                  pl='4px'>
                  <Avatar
                    alt={user.name}
                    src={user.imgSrc}
                    sx={{ width: 40, height: 40 }}
                  />
                  <Stack>
                    <Typography variant='body1'>{user.name}</Typography>
                    <Typography variant='body2' color='textSecondary'>
                      {user.position}
                    </Typography>
                  </Stack>
                </Stack>
              </>
            )}
          </Stack>
        </Stack>
      </Menu>
    </Box>
  );
};
